// File: frontend/src/components/common/SmoothScroll.js

import { useEffect } from 'react';
import Lenis from 'lenis';
import { useLocation } from 'react-router-dom';

/**
 * Provides buttery-smooth scrolling via Lenis.
 * Optimized for mobile performance.
 * Disabled on routes with custom scroll containers (e.g., chat pages).
 */
function SmoothScroll() {
  const location = useLocation();

  // Routes where Lenis should be disabled
  const DISABLED_ROUTES = ['/dashboard/chat/', '/chat/'];
  const isDisabled = DISABLED_ROUTES.some(route => location.pathname.includes(route));

  useEffect(() => {
    if (isDisabled) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    const lenis = new Lenis({
      // Faster on mobile, smoother on desktop
      duration: isMobile ? 0.5 : 0.8,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      // More responsive on mobile
      smoothWheel: !isMobile, // Disable smooth wheel on mobile for native feel
      wheelMultiplier: isMobile ? 1.3 : 1.3,
      touchMultiplier: isMobile ? 2.0 : 1.5,
      infinite: false,
      // Improve mobile performance
      autoResize: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Expose lenis on window for external control
    window.__lenis = lenis;

    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, [location.pathname, isDisabled]);

  // Scroll to top on route change
  useEffect(() => {
    if (isDisabled) return;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, isDisabled]);

  return null;
}

export default SmoothScroll;